/* Product gallery (snippets/kiids-pdp-gallery.liquid): the track scrolls with native swipe (scroll-snap); dots, thumbnails and
   the "N / total" counter follow it. A tap on a photo opens Ella's bundled PhotoSwipe full screen (pinch and double-tap zoom,
   swipe down to close); closing leaves the track on the last viewed photo. Back (Android, browser) closes the viewer, not the page. */
class KiidsGallery extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-track]');
    this.slides = [...this.track.children];
    this.track.addEventListener('scroll', () => requestAnimationFrame(() => this.mark(Math.round(this.track.scrollLeft / this.track.clientWidth))), { passive: true });
    this.addEventListener('click', (e) => {
      const thumb = e.target.closest('.kiids-pdp__thumb');
      if (thumb) this.show(thumb.dataset.mediaId, true);
      const zoom = e.target.closest('[data-zoom]');
      if (zoom && !(e.metaKey || e.ctrlKey || e.shiftKey || e.button)) { e.preventDefault(); this.open(zoom); } // modified clicks keep "open in new tab"
    });
    this.show(this.dataset.start);
  }

  show(id, smooth) {
    const i = this.slides.findIndex((s) => s.dataset.mediaId == id);
    if (i < 0) return;
    const reduce = matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.track.scrollTo({ left: i * this.track.clientWidth, behavior: smooth && !reduce ? 'smooth' : 'instant' });
    this.mark(i);
  }

  mark(i) {
    const id = this.slides[i]?.dataset.mediaId;
    if (!id || id === this.current) return;
    this.current = id;
    this.querySelectorAll('.kiids-pdp__thumb').forEach((b) => b.toggleAttribute('aria-current', b.dataset.mediaId == id));
    const count = this.querySelector('[data-count]');
    if (count) count.textContent = `${i + 1} / ${this.slides.length}`;
  }

  async open(link) {
    if (this.busy) return;
    this.busy = true;
    link.setAttribute('aria-busy', 'true'); // first open downloads PhotoSwipe; the photo dims until the viewer shows
    const urls = window.photoswipeUrls;
    if (!document.getElementById('photoswipe-css')) document.head.append(Object.assign(document.createElement('link'), { id: 'photoswipe-css', rel: 'stylesheet', href: urls.css }));
    const links = [...this.querySelectorAll('[data-zoom]')];
    const d = this.dataset;
    try {
      // Both modules up front: a failed core import inside loadAndOpen() would be an unhandled rejection, not this catch.
      const [{ default: Lightbox }, { default: PhotoSwipe }] = await Promise.all([import(urls.lightboxJs), import(urls.coreJs)]);
      const lb = new Lightbox({
        dataSource: links.map((a) => ({ src: a.href, width: +a.dataset.w, height: +a.dataset.h, msrc: a.querySelector('img')?.currentSrc, element: a })),
        pswpModule: PhotoSwipe,
        mainClass: 'kiids-pswp', bgOpacity: 1, zoom: false, padding: { top: 64, bottom: 24, left: 12, right: 12 },
        secondaryZoomLevel: 2, maxZoomLevel: 4, closeTitle: d.close, arrowPrevTitle: d.prev, arrowNextTitle: d.next, errorMsg: d.error,
        indexIndicatorSep: ' / ', returnFocus: false,
      });
      lb.on('change', () => this.show(links[lb.pswp.currIndex].closest('[data-media-id]').dataset.mediaId));
      // Back closes the viewer: one history entry while it is open, removed again when it closes another way.
      const onPop = () => { const p = lb.pswp; if (p) p.opener.isOpening ? p.on('openingAnimationEnd', () => p.close()) : p.close(); }; // close() is ignored mid-opening
      lb.on('afterInit', () => { history.pushState({ kiidsZoom: 1 }, ''); addEventListener('popstate', onPop); });
      lb.on('close', () => { removeEventListener('popstate', onPop); if (history.state?.kiidsZoom) history.back(); });
      // Focus returns to the photo now on the page (PhotoSwipe would focus the tapped one and scroll the page to it).
      lb.on('destroy', () => { this.busy = false; this.querySelector(`[data-media-id="${this.current}"] [data-zoom]`)?.focus({ preventScroll: true }); });
      lb.init();
      lb.on('afterInit', () => link.removeAttribute('aria-busy'));
      lb.loadAndOpen(links.indexOf(link));
    } catch (err) {
      this.busy = false;
      link.removeAttribute('aria-busy');
      console.error('kiids-gallery: PhotoSwipe failed to load', err);
      location.href = link.href; // the large photo on its own is still better than a dead tap
    }
  }
}
if (!customElements.get('kiids-gallery')) customElements.define('kiids-gallery', KiidsGallery);
